import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { UsersRound, UserRoundSearch, Share } from "lucide-react";
import { toast } from "sonner";
import { createColumnHelper } from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import { ApolloError } from "@apollo/client";
import { DataTable } from "@/components/DataTable";
import { NameAndAvatar } from "@/components/StudentListWrapper/Grid/NameAndAvatar";
import {
  FUNNEL_STATE_TRANSLATION_KEYS,
  FunnelStateMaps,
} from "@/util/funnelGroups";
import { Tabs, TabsList, TabsTrigger, Button } from "@/components/ui";
import { EmptyStateComponent } from "@/components/EmptyStateComponent";
import {
  usePredictiveIndexStudents,
  PredictiveIndexStudent,
  IndexType,
} from "@/hooks/usePredictiveIndexStudents";
import {
  PredictionIndexEnum,
  RisePredictiveIndexEnum,
  SortDirectionEnum,
  StudentListCommitIndexSortableFieldEnum,
  StudentListRisePredictiveIndexSortableFieldEnum,
  StudentListOrganizationSortableFieldEnum,
  FunnelStateEnum,
  PartnerFeatureEnum,
} from "@/__generated__/types";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { PredictiveModelType } from "./types";

interface ListSectionProps {
  orgId: string | undefined;
  orgIdLoading: boolean;
  orgIdError: ApolloError | undefined;
  modelType?: PredictiveModelType;
}

type NonRiseTabValue = "high" | "medium" | "low";
type RiseTabValue = "high" | "mediumHigh" | "medium" | "mediumLow" | "low";
type TabValue = NonRiseTabValue | RiseTabValue;

interface TabData {
  students: PredictiveIndexStudent[];
  loading: boolean;
  error: unknown;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
  sortField?:
    | StudentListCommitIndexSortableFieldEnum
    | StudentListRisePredictiveIndexSortableFieldEnum
    | StudentListOrganizationSortableFieldEnum;
  sortDirection: SortDirectionEnum;
  previousIndexFilter?: PredictionIndexEnum | RisePredictiveIndexEnum;
  funnelStatesFilter?: FunnelStateEnum[];
  inCommunityFilter?: boolean;
  hasLoadedBefore?: boolean;
  currentPageNumber: number;
}

const ITEMS_PER_PAGE = 5;

const TAB_INDEX_MAP: Record<TabValue, string> = {
  high: "high",
  mediumHigh: "medium-high",
  medium: "medium",
  mediumLow: "medium-low",
  low: "low",
};

const NON_RISE_FUNNEL_STATES: FunnelStateEnum[] = [
  FunnelStateEnum.Inquired,
  FunnelStateEnum.ApplicationStarted,
  FunnelStateEnum.ApplicationComplete,
  FunnelStateEnum.Denied,
  FunnelStateEnum.Waitlisted,
  FunnelStateEnum.Deferred,
  FunnelStateEnum.Accepted,
  FunnelStateEnum.AcceptanceDeclined,
  FunnelStateEnum.Committed,
  FunnelStateEnum.CommitmentWithdrawn,
  FunnelStateEnum.Enrolled,
];

const columnHelper = createColumnHelper<PredictiveIndexStudent>();

const createInitialTabData = (): TabData => ({
  students: [],
  loading: false,
  error: null,
  totalCount: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  startCursor: null,
  endCursor: null,
  sortField: undefined,
  sortDirection: SortDirectionEnum.Desc,
  hasLoadedBefore: false,
  currentPageNumber: 1,
});

export const ListSection: React.FC<ListSectionProps> = ({
  orgId,
  orgIdLoading,
  orgIdError,
  modelType = "Commit",
}) => {
  const { t } = useTranslation();
  const { hasOrgFeature } = useOrgPermissions();
  const isRiseEnabled = hasOrgFeature(PartnerFeatureEnum.RisePredictiveModels);

  const getIndexType = useCallback(
    (type: PredictiveModelType): IndexType => {
      const lowerType = type.toLowerCase();

      if (isRiseEnabled) {
        if (lowerType === "commit") return "riseCommit";
        if (lowerType === "apply") return "riseApply";
        if (lowerType === "melt") return "riseMelt";
        if (lowerType === "yield") return "riseYield";
      }

      return "commit";
    },
    [isRiseEnabled],
  );

  const indexType = getIndexType(modelType);

  const [activeTabByModel, setActiveTabByModel] = useState<
    Record<IndexType, TabValue>
  >({
    commit: "high",
    riseCommit: "high",
    riseApply: "high",
    riseMelt: "high",
    riseYield: "high",
    riseRetain: "high",
  });

  const activeTab = activeTabByModel[indexType];

  const initialTabData = useMemo<Record<TabValue, TabData>>(() => {
    if (isRiseEnabled) {
      return {
        high: createInitialTabData(),
        mediumHigh: createInitialTabData(),
        medium: createInitialTabData(),
        mediumLow: createInitialTabData(),
        low: createInitialTabData(),
      };
    }
    return {
      high: createInitialTabData(),
      medium: createInitialTabData(),
      low: createInitialTabData(),
      mediumHigh: createInitialTabData(),
      mediumLow: createInitialTabData(),
    };
  }, [isRiseEnabled]);

  const [tabData, setTabData] =
    useState<Record<TabValue, TabData>>(initialTabData);

  const currentTabData = tabData[activeTab];

  const getCurrentPredictionIndex = useCallback(
    (tab: TabValue): PredictionIndexEnum | RisePredictiveIndexEnum => {
      if (isRiseEnabled) {
        switch (tab) {
          case "high":
            return RisePredictiveIndexEnum.High;
          case "mediumHigh":
            return RisePredictiveIndexEnum.MediumHigh;
          case "medium":
            return RisePredictiveIndexEnum.Medium;
          case "mediumLow":
            return RisePredictiveIndexEnum.MediumLow;
          case "low":
            return RisePredictiveIndexEnum.Low;
          default:
            return RisePredictiveIndexEnum.High;
        }
      } else {
        switch (tab) {
          case "high":
            return PredictionIndexEnum.High;
          case "medium":
            return PredictionIndexEnum.Medium;
          case "low":
            return PredictionIndexEnum.Low;
          default:
            return PredictionIndexEnum.High;
        }
      }
    },
    [isRiseEnabled],
  );

  const [currentFilters, setCurrentFilters] = useState<{
    previousIndexFilter?: PredictionIndexEnum | RisePredictiveIndexEnum;
    funnelStatesFilter?: FunnelStateEnum[];
    inCommunityFilter?: boolean;
  }>({});

  const [selectedFilterValues, setSelectedFilterValues] = useState<
    Record<string, string>
  >({});

  const [activeFilterId, setActiveFilterId] = useState<string | null>(null);

  const funnelStateMapping = useMemo(() => {
    if (isRiseEnabled) {
      return Object.entries(FunnelStateMaps.SIDEBAR).map(
        ([groupKey, states]) => ({
          value: t(`funnelGroups.${groupKey}`),
          enumValues: states,
        }),
      );
    } else {
      return NON_RISE_FUNNEL_STATES.map((state) => ({
        value: t(`global.funnelStates.${FUNNEL_STATE_TRANSLATION_KEYS[state]}`),
        enumValues: [state],
      }));
    }
  }, [t, isRiseEnabled]);

  const [paginationParams, setPaginationParams] = useState<{
    after?: string;
    before?: string;
  }>({});

  const filterOptions = useMemo(() => {
    const getAvailableIndexOptions = () => {
      if (isRiseEnabled) {
        const allIndices = [
          { value: t("predictiveModels.list.buckets.high"), key: "high" },
          {
            value: t("predictiveModels.list.buckets.mediumHigh"),
            key: "mediumHigh",
          },
          { value: t("predictiveModels.list.buckets.medium"), key: "medium" },
          {
            value: t("predictiveModels.list.buckets.mediumLow"),
            key: "mediumLow",
          },
          { value: t("predictiveModels.list.buckets.low"), key: "low" },
        ];
        return allIndices.filter((index) => {
          return index.key !== activeTab;
        });
      } else {
        const allIndices = [
          { value: t("predictiveModels.list.buckets.high"), key: "high" },
          { value: t("predictiveModels.list.buckets.medium"), key: "medium" },
          { value: t("predictiveModels.list.buckets.low"), key: "low" },
        ];
        return allIndices.filter((index) => {
          return index.key !== activeTab;
        });
      }
    };

    const indexOptions = getAvailableIndexOptions();

    const filters = [
      {
        id: "previousIndex",
        label: t("predictiveModels.list.previousBucket"),
        values: indexOptions.map((opt) => ({ value: opt.value })),
      },
      {
        id: "funnelState",
        label: t("predictiveModels.list.funnelStateFilter"),
        values: funnelStateMapping.map((opt) => ({ value: opt.value })),
      },
    ];

    if (isRiseEnabled) {
      filters.push({
        id: "communityStatus",
        label: t("predictiveModels.list.communityStatus"),
        values: [
          { value: t("predictiveModels.list.inCommunity") },
          { value: t("predictiveModels.list.notInCommunity") },
        ],
      });
    }

    return { filters, indexMapping: indexOptions };
  }, [t, activeTab, isRiseEnabled, funnelStateMapping]);

  const { students, loading, error, totalCount, pageInfo } =
    usePredictiveIndexStudents({
      orgId,
      orgIdLoading,
      orgIdError,
      indexType,
      currentIndex: getCurrentPredictionIndex(activeTab),
      previousIndex: currentFilters.previousIndexFilter,
      funnelStates: currentFilters.funnelStatesFilter,
      inCommunity: currentFilters.inCommunityFilter,
      sortField: currentTabData.sortField || undefined,
      sortDirection: currentTabData.sortDirection,
      first: paginationParams.before ? undefined : ITEMS_PER_PAGE,
      last: paginationParams.before ? ITEMS_PER_PAGE : undefined,
      after: paginationParams.after,
      before: paginationParams.before,
    });

  const lastActiveTabRef = useRef<TabValue>(activeTab);

  useEffect(() => {
    lastActiveTabRef.current = activeTab;
  }, [activeTab, indexType]);

  useEffect(() => {
    if (lastActiveTabRef.current === activeTab) {
      setTabData((prev) => {
        const prevTabData = prev[activeTab];
        const bestTotalCount = totalCount || prevTabData.totalCount || 0;

        return {
          ...prev,
          [activeTab]: {
            ...prevTabData,
            students: loading ? prevTabData.students : students,
            loading,
            error,
            totalCount: bestTotalCount,
            hasNextPage: loading
              ? prevTabData.hasNextPage
              : pageInfo.hasNextPage,
            hasPreviousPage: loading
              ? prevTabData.hasPreviousPage
              : pageInfo.hasPreviousPage,
            startCursor: loading
              ? prevTabData.startCursor
              : pageInfo.startCursor,
            endCursor: loading ? prevTabData.endCursor : pageInfo.endCursor,
            hasLoadedBefore: prevTabData.hasLoadedBefore || !loading,
            lastKnownTotalCount: bestTotalCount,
          },
        };
      });
    }
  }, [activeTab, students, loading, error, totalCount, pageInfo]);

  const handleTabChange = (value: string) => {
    setActiveTabByModel((prev) => ({
      ...prev,
      [indexType]: value as TabValue,
    }));
  };

  const handleSortChange = useCallback(
    (
      sortField:
        | StudentListCommitIndexSortableFieldEnum
        | StudentListRisePredictiveIndexSortableFieldEnum
        | StudentListOrganizationSortableFieldEnum,
      sortDirection: SortDirectionEnum,
    ) => {
      setTabData((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          sortField,
          sortDirection,
          currentPageNumber: 1,
        },
      }));
      setPaginationParams({});
    },
    [activeTab],
  );

  const handleNextPage = useCallback(() => {
    if (currentTabData.hasNextPage && currentTabData.endCursor) {
      setPaginationParams({
        after: currentTabData.endCursor,
        before: undefined,
      });

      setTabData((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          currentPageNumber: prev[activeTab].currentPageNumber + 1,
        },
      }));
    }
  }, [currentTabData.hasNextPage, currentTabData.endCursor, activeTab]);

  const handlePreviousPage = useCallback(() => {
    if (currentTabData.hasPreviousPage && currentTabData.startCursor) {
      setPaginationParams({
        after: undefined,
        before: currentTabData.startCursor,
      });

      setTabData((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          currentPageNumber: Math.max(1, prev[activeTab].currentPageNumber - 1),
        },
      }));
    }
  }, [currentTabData.hasPreviousPage, currentTabData.startCursor, activeTab]);

  const getIndexDisplayName = useCallback(
    (index: PredictionIndexEnum | RisePredictiveIndexEnum | null): string => {
      if (!index) return t("predictiveModels.list.buckets.none");

      switch (index) {
        case PredictionIndexEnum.High:
        case RisePredictiveIndexEnum.High:
          return t("predictiveModels.list.buckets.high");
        case RisePredictiveIndexEnum.MediumHigh:
          return t("predictiveModels.list.buckets.mediumHigh");
        case PredictionIndexEnum.Medium:
        case RisePredictiveIndexEnum.Medium:
          return t("predictiveModels.list.buckets.medium");
        case RisePredictiveIndexEnum.MediumLow:
          return t("predictiveModels.list.buckets.mediumLow");
        case PredictionIndexEnum.Low:
        case RisePredictiveIndexEnum.Low:
          return t("predictiveModels.list.buckets.low");
        default:
          return t("predictiveModels.list.buckets.none");
      }
    },
    [t],
  );

  const formatDate = useCallback(
    (date: string | null): string => {
      if (!date) return t("predictiveModels.list.never");
      const dateWithTime = date.includes("T") ? date : date + "T00:00:00";
      return new Date(dateWithTime).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    [t],
  );

  const getTabTitle = useCallback(
    (tab: TabValue): string => {
      switch (tab) {
        case "high":
          return t("predictiveModels.list.tabs.highTitle");
        case "mediumHigh":
          return t("predictiveModels.list.tabs.mediumHighTitle");
        case "medium":
          return t("predictiveModels.list.tabs.mediumTitle");
        case "mediumLow":
          return t("predictiveModels.list.tabs.mediumLowTitle");
        case "low":
          return t("predictiveModels.list.tabs.lowTitle");
        default:
          return t("predictiveModels.list.tabs.highTitle");
      }
    },
    [t],
  );

  const getTabSubtitle = useCallback(
    (tab: TabValue): string => {
      const currentDate = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      const subtitleKeyMap: Record<TabValue, string> = {
        high: "highSubtitle",
        mediumHigh: "mediumHighSubtitle",
        medium: "mediumSubtitle",
        mediumLow: "mediumLowSubtitle",
        low: "lowSubtitle",
      };

      const indexKeyMap: Record<PredictiveModelType, string> = {
        Apply: "predictiveModels.tabs.applyIndex",
        Commit: "predictiveModels.tabs.commitIndex",
        Melt: "predictiveModels.tabs.meltIndex",
        Yield: "predictiveModels.tabs.yieldIndex",
      };

      const subtitleKey = subtitleKeyMap[tab];
      const indexTranslation = t(indexKeyMap[modelType]);

      return t(`predictiveModels.list.tabs.${subtitleKey}`, {
        index: indexTranslation,
        date: currentDate,
      });
    },
    [t, modelType],
  );

  const getChangedColumnName = useCallback(
    (tab: TabValue): string => {
      return t("predictiveModels.list.columns.changedTo", {
        index: TAB_INDEX_MAP[tab] || "high",
      });
    },
    [t],
  );

  const { navToRoute } = useProtectedNavigation();

  const routeToStudent = useCallback(
    (studentId: string) => {
      navToRoute("student", { studentId }, {}, { newTab: true });
    },
    [navToRoute],
  );

  const getSortFieldForChangedColumn = useCallback(
    (
      tab: TabValue,
    ):
      | StudentListCommitIndexSortableFieldEnum
      | StudentListRisePredictiveIndexSortableFieldEnum => {
      if (isRiseEnabled) {
        switch (tab) {
          case "high":
            return StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexChangedToHighAt;
          case "mediumHigh":
            return StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexChangedToMediumHighAt;
          case "medium":
            return StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexChangedToMediumAt;
          case "mediumLow":
            return StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexChangedToMediumLowAt;
          case "low":
            return StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexChangedToLowAt;
          default:
            return StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexChangedToHighAt;
        }
      } else {
        switch (tab) {
          case "high":
            return StudentListCommitIndexSortableFieldEnum.CommitIndexChangedToHighAt;
          case "medium":
            return StudentListCommitIndexSortableFieldEnum.CommitIndexChangedToMediumAt;
          case "low":
            return StudentListCommitIndexSortableFieldEnum.CommitIndexChangedToLowAt;
          default:
            return StudentListCommitIndexSortableFieldEnum.CommitIndexChangedToHighAt;
        }
      }
    },
    [isRiseEnabled],
  );

  const columns = useMemo<ColumnDef<PredictiveIndexStudent, unknown>[]>(() => {
    const baseColumns: ColumnDef<PredictiveIndexStudent, unknown>[] = [
      columnHelper.accessor((row) => `${row.name}`, {
        id: "name",
        size: 350,
        header: ({ column }) => (
          <DataTable.THeaderCellSorting
            column={column}
            onSortingChange={({
              orderingDirection,
            }: {
              orderingDirection: SortDirectionEnum;
            }) => {
              const sortField = isRiseEnabled
                ? StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexName
                : StudentListOrganizationSortableFieldEnum.Name;
              handleSortChange(sortField, orderingDirection);
            }}
          >
            {t("predictiveModels.list.columns.name")}
          </DataTable.THeaderCellSorting>
        ),
        cell: ({ row }) => (
          <DataTable.TBodyCell>
            <NameAndAvatar
              name={row.original.name}
              imageUrl={
                row.original.imageUrl
                  ? { url: row.original.imageUrl }
                  : undefined
              }
              avatarShape="square"
              avatarSize="size-12"
            />
          </DataTable.TBodyCell>
        ),
      }) as ColumnDef<PredictiveIndexStudent, unknown>,

      columnHelper.accessor("score", {
        id: "score",
        header: ({ column }) => (
          <DataTable.THeaderCellSorting
            column={column}
            onSortingChange={({
              orderingDirection,
            }: {
              orderingDirection: SortDirectionEnum;
            }) => {
              const sortField = isRiseEnabled
                ? StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexScore
                : StudentListCommitIndexSortableFieldEnum.CommitScore;
              handleSortChange(sortField, orderingDirection);
            }}
          >
            {t("predictiveModels.list.columns.score")}
          </DataTable.THeaderCellSorting>
        ),
        cell: ({ getValue }) => (
          <DataTable.TBodyCell>{Math.round(getValue())}</DataTable.TBodyCell>
        ),
      }) as ColumnDef<PredictiveIndexStudent, unknown>,

      columnHelper.accessor("indexChangedOn", {
        id: "changedToIndex",
        minSize: 215,
        header: ({ column }) => (
          <DataTable.THeaderCellSorting
            column={column}
            onSortingChange={({
              orderingDirection,
            }: {
              orderingDirection: SortDirectionEnum;
            }) => {
              const sortField = getSortFieldForChangedColumn(activeTab);
              handleSortChange(sortField, orderingDirection);
            }}
          >
            {getChangedColumnName(activeTab)}
          </DataTable.THeaderCellSorting>
        ),
        cell: ({ getValue }) => (
          <DataTable.TBodyCell>{formatDate(getValue())}</DataTable.TBodyCell>
        ),
      }) as ColumnDef<PredictiveIndexStudent, unknown>,

      columnHelper.accessor("previousIndex", {
        id: "previousIndex",
        header: ({ column }) => (
          <DataTable.THeaderCellSorting
            column={column}
            onSortingChange={({
              orderingDirection,
            }: {
              orderingDirection: SortDirectionEnum;
            }) => {
              const sortField = isRiseEnabled
                ? StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexPreviousIndex
                : StudentListCommitIndexSortableFieldEnum.PreviousCommitIndex;
              handleSortChange(sortField, orderingDirection);
            }}
          >
            {t("predictiveModels.list.columns.previousBucket")}
          </DataTable.THeaderCellSorting>
        ),
        cell: ({ getValue }) => (
          <DataTable.TBodyCell>
            {getIndexDisplayName(getValue())}
          </DataTable.TBodyCell>
        ),
      }) as ColumnDef<PredictiveIndexStudent, unknown>,

      columnHelper.accessor("funnelState", {
        id: "funnelState",
        header: ({ column }) => (
          <DataTable.THeaderCellSorting
            column={column}
            onSortingChange={({
              orderingDirection,
            }: {
              orderingDirection: SortDirectionEnum;
            }) => {
              const sortField = isRiseEnabled
                ? StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexFunnelState
                : StudentListOrganizationSortableFieldEnum.FunnelState;
              handleSortChange(sortField, orderingDirection);
            }}
          >
            {t("predictiveModels.list.columns.funnelState")}
          </DataTable.THeaderCellSorting>
        ),
        cell: ({ getValue }) => (
          <DataTable.TBodyCell>
            {t(
              `global.funnelStates.${FUNNEL_STATE_TRANSLATION_KEYS[getValue()]}`,
            )}
          </DataTable.TBodyCell>
        ),
      }) as ColumnDef<PredictiveIndexStudent, unknown>,
    ];

    if (isRiseEnabled) {
      baseColumns.push(
        columnHelper.accessor("inCommunity", {
          id: "communityStatus",
          header: ({ column }) => (
            <DataTable.THeaderCellSorting
              column={column}
              onSortingChange={({
                orderingDirection,
              }: {
                orderingDirection: SortDirectionEnum;
              }) =>
                handleSortChange(
                  StudentListRisePredictiveIndexSortableFieldEnum.RisePredictiveIndexCommunityStatus,
                  orderingDirection,
                )
              }
            >
              {t("predictiveModels.list.communityStatus")}
            </DataTable.THeaderCellSorting>
          ),
          cell: ({ getValue }) => (
            <DataTable.TBodyCell>
              {getValue()
                ? t("predictiveModels.list.inCommunity")
                : t("predictiveModels.list.notInCommunity")}
            </DataTable.TBodyCell>
          ),
        }) as ColumnDef<PredictiveIndexStudent, unknown>,
      );
    }

    baseColumns.push(
      columnHelper.display({
        id: "actions",
        header: () => <DataTable.THeaderCell>{""}</DataTable.THeaderCell>,
        cell: ({ row }) => (
          <DataTable.TBodyCell isLastCell={true}>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                routeToStudent(row.original.id);
              }}
            >
              {t("predictiveModels.list.view")}
            </Button>
          </DataTable.TBodyCell>
        ),
      }) as ColumnDef<PredictiveIndexStudent, unknown>,
    );

    return baseColumns;
  }, [
    activeTab,
    handleSortChange,
    t,
    formatDate,
    getIndexDisplayName,
    getChangedColumnName,
    getSortFieldForChangedColumn,
    routeToStudent,
    isRiseEnabled,
  ]);

  const handleFilterApply = useCallback(
    (filterId: string, values: string[]) => {
      if (values.length === 0) {
        setSelectedFilterValues((prev) =>
          Object.fromEntries(
            Object.entries(prev).filter(([key]) => key !== filterId),
          ),
        );
        setActiveFilterId(null);

        if (filterId === "previousIndex") {
          setCurrentFilters((prev) => ({
            ...prev,
            previousIndexFilter: undefined,
          }));
        } else if (filterId === "funnelState") {
          setCurrentFilters((prev) => ({
            ...prev,
            funnelStatesFilter: undefined,
          }));
        } else if (filterId === "communityStatus") {
          setCurrentFilters((prev) => ({
            ...prev,
            inCommunityFilter: undefined,
          }));
        }
        setPaginationParams({});
        return;
      }

      const value = values[0];

      setSelectedFilterValues((prev) => ({
        ...prev,
        [filterId]: value,
      }));

      setActiveFilterId(filterId);

      if (filterId === "previousIndex") {
        const indexOption = filterOptions.indexMapping.find(
          (item) => item.value === value,
        );

        if (indexOption) {
          if (isRiseEnabled) {
            const riseEnumMap: Record<string, RisePredictiveIndexEnum> = {
              high: RisePredictiveIndexEnum.High,
              mediumHigh: RisePredictiveIndexEnum.MediumHigh,
              medium: RisePredictiveIndexEnum.Medium,
              mediumLow: RisePredictiveIndexEnum.MediumLow,
              low: RisePredictiveIndexEnum.Low,
            };
            const enumValue = riseEnumMap[indexOption.key];
            setCurrentFilters((prev) => ({
              ...prev,
              previousIndexFilter: enumValue,
            }));
          } else {
            const nonRiseEnumMap: Record<string, PredictionIndexEnum> = {
              high: PredictionIndexEnum.High,
              medium: PredictionIndexEnum.Medium,
              low: PredictionIndexEnum.Low,
            };
            const enumValue = nonRiseEnumMap[indexOption.key];
            setCurrentFilters((prev) => ({
              ...prev,
              previousIndexFilter: enumValue,
            }));
          }
        }
      } else if (filterId === "funnelState") {
        const funnelStateOption = funnelStateMapping.find(
          (item) => item.value === value,
        );

        if (funnelStateOption) {
          setCurrentFilters((prev) => ({
            ...prev,
            funnelStatesFilter: funnelStateOption.enumValues,
          }));
        }
      } else if (filterId === "communityStatus") {
        const inCommunityValue =
          value === t("predictiveModels.list.inCommunity");
        setCurrentFilters((prev) => ({
          ...prev,
          inCommunityFilter: inCommunityValue,
        }));
      }
      setPaginationParams({});

      setTabData((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          currentPageNumber: 1,
        },
      }));
    },
    [
      activeTab,
      isRiseEnabled,
      t,
      filterOptions.indexMapping,
      funnelStateMapping,
    ],
  );

  const getFilterDisplayValue = useCallback((): string | undefined => {
    if (!activeFilterId) return undefined;
    return selectedFilterValues[activeFilterId];
  }, [activeFilterId, selectedFilterValues]);

  const topHeader = useMemo(() => {
    const tabTriggers = isRiseEnabled
      ? [
          { value: "high", label: t("predictiveModels.list.buckets.high") },
          {
            value: "mediumHigh",
            label: t("predictiveModels.list.buckets.mediumHigh"),
          },
          { value: "medium", label: t("predictiveModels.list.buckets.medium") },
          {
            value: "mediumLow",
            label: t("predictiveModels.list.buckets.mediumLow"),
          },
          { value: "low", label: t("predictiveModels.list.buckets.low") },
        ]
      : [
          { value: "high", label: t("predictiveModels.list.buckets.high") },
          { value: "medium", label: t("predictiveModels.list.buckets.medium") },
          { value: "low", label: t("predictiveModels.list.buckets.low") },
        ];

    return (
      <div className="flex w-full items-start justify-between p-6 pr-0">
        <div className="flex flex-col space-y-4">
          <DataTable.Title
            title={getTabTitle(activeTab)}
            description={getTabSubtitle(activeTab)}
            className="p-0"
          />
          <TabsList
            className={`flex h-10 items-center p-1 ${isRiseEnabled ? "w-[500px]" : "w-80"}`}
          >
            {tabTriggers.map((trigger) => (
              <TabsTrigger
                key={trigger.value}
                value={trigger.value}
                className="flex-1 items-center justify-center gap-2 px-3 py-1.5"
              >
                {trigger.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <DataTable.Filter
            customFilters={filterOptions.filters}
            onFilterChange={handleFilterApply}
            displayValue={getFilterDisplayValue()}
          />
          <DataTable.HeaderActions
            actions={[
              {
                key: "share",
                render: () => (
                  <>
                    <Share
                      aria-hidden
                      height={16}
                      className="text-foreground"
                    />
                    {t("predictiveModels.list.menuOptions.share")}
                  </>
                ),
                onClick: () => {
                  toast.success(
                    t("predictiveModels.list.menuOptions.successCopied"),
                  );
                  navigator.clipboard.writeText(window.location.href);
                },
              },
            ]}
          />
        </div>
      </div>
    );
  }, [
    activeTab,
    t,
    filterOptions.filters,
    handleFilterApply,
    getFilterDisplayValue,
    getTabTitle,
    getTabSubtitle,
    isRiseEnabled,
  ]);

  const displayData =
    currentTabData.hasLoadedBefore && !loading
      ? currentTabData.students
      : loading
        ? []
        : students;

  const displayTotalCount = currentTabData.totalCount || 0;

  const hasAnyError = orgIdError || error;
  const hasNoOrgId = !orgId && !orgIdLoading;

  const displayTotalPages = Math.ceil(displayTotalCount / ITEMS_PER_PAGE);

  const isEmptyOrErrorState =
    hasAnyError || hasNoOrgId || (!loading && displayData.length === 0);

  const shouldDisablePagination =
    isEmptyOrErrorState || (loading && displayTotalCount === 0);

  const getEmptyState = () => {
    if (hasAnyError || hasNoOrgId) {
      return (
        <div className="flex h-[458px] items-center justify-center">
          <EmptyStateComponent
            message={t("predictiveModels.list.errorLoadingData")}
            icon={<UsersRound className="size-12" />}
          />
        </div>
      );
    }

    if (loading && !currentTabData.hasLoadedBefore) {
      return undefined;
    }

    if (!loading && displayData.length === 0) {
      return (
        <div className="flex h-[458px] items-center justify-center">
          <EmptyStateComponent
            message={t("predictiveModels.list.emptyState")}
            icon={<UserRoundSearch className="size-12" />}
          />
        </div>
      );
    }

    return undefined;
  };

  return (
    <div className="space-y-6">
      <div className="flex h-[625px] flex-col rounded-lg border shadow-sm">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="flex h-full flex-col"
        >
          <DataTable
            columns={columns}
            data={displayData}
            isLoading={loading}
            emptyState={getEmptyState()}
            topHeader={topHeader}
            tableHeader={<DataTable.THeader />}
            tableBody={
              <DataTable.TBody
                onRowClick={(row) =>
                  routeToStudent((row.original as PredictiveIndexStudent).id)
                }
              />
            }
            tableContentClassName="h-[372px]"
            pagination={
              <>
                <DataTable.PaginationLabel
                  pageCount={displayTotalPages}
                  pageIndex={currentTabData.currentPageNumber - 1}
                  hasLoadedBefore={currentTabData.hasLoadedBefore}
                />
                <DataTable.PaginationControls
                  onPreviousPage={
                    shouldDisablePagination
                      ? false
                      : currentTabData.hasPreviousPage
                        ? handlePreviousPage
                        : false
                  }
                  onNextPage={
                    shouldDisablePagination
                      ? false
                      : currentTabData.hasNextPage
                        ? handleNextPage
                        : false
                  }
                />
              </>
            }
          />
        </Tabs>
      </div>
    </div>
  );
};
